'use client';

import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';

interface UsePortalOptions {
  isOpen?: boolean;
  closeOnEsc?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
}

interface UsePortalReturn {
  openPortal: () => void;
  closePortal: () => void;
  isOpen: boolean;
  Portal: ({ children }: { children: ReactNode }) => ReturnType<typeof createPortal> | null;
}

export const usePortal = ({
  isOpen: initialIsOpen = false,
  closeOnEsc = true,
  onClose,
  onOpen,
}: UsePortalOptions = {}): UsePortalReturn => {
  const hasClientRendered = useClientHasRendered();

  const [isOpen, setIsOpen] = useState(initialIsOpen);
  const previousIsOpen = useRef(initialIsOpen);

  const openPortal = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closePortal = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEsc) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closePortal();
      }
    };
    if (hasClientRendered) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, closeOnEsc, closePortal, hasClientRendered]);

  // Call onOpen/onClose callbacks when state changes
  useEffect(() => {
    if (isOpen && !previousIsOpen.current) {
      onOpen?.();
    } else if (!isOpen && previousIsOpen.current) {
      onClose?.();
    }
    previousIsOpen.current = isOpen;
  }, [isOpen, onOpen, onClose]);

  // Portal component that renders to document.body
  // Memoized to prevent recreating the component function on every render
  const Portal = useMemo(
    () =>
      ({ children }: { children: ReactNode }): ReturnType<typeof createPortal> | null => {
        if (!isOpen || !hasClientRendered) {
          return null;
        }

        return createPortal(<>{children}</>, document.body);
      },
    [isOpen, hasClientRendered],
  );

  return {
    openPortal,
    closePortal,
    isOpen,
    Portal,
  };
};
